import ApplicationAdapter from './application';
import type {
  ContentSuggestion,
  SuggestionSet,
  SuggestionRun,
} from 'admin-panel/utils/content-suggestions';

// Validate the display contract at the API boundary; never coerce null into a removal.
function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Invalid suggestion response');
  }
  return value as Record<string, unknown>;
}

function string(value: unknown): string {
  if (typeof value !== 'string') throw new Error('Invalid suggestion text');
  return value;
}

function array(value: unknown, allowEmpty = false): unknown[] {
  if (!Array.isArray(value) || (!allowEmpty && !value.length))
    throw new Error('Invalid suggestion collection');
  return value;
}

export function normalizeSuggestion(value: unknown): ContentSuggestion {
  const row = object(value);
  const { type, priority, confidence } = row;
  if (
    (type !== 'consistency' &&
      type !== 'freshness' &&
      type !== 'writing-quality' &&
      type !== 'missing-info') ||
    (priority !== 'high' && priority !== 'medium' && priority !== 'low') ||
    typeof confidence !== 'number' ||
    !Number.isFinite(confidence) ||
    confidence < 0 ||
    confidence > 1
  )
    throw new Error('Invalid suggestion assessment');
  return {
    id: string(row['id']),
    type,
    title: string(row['title']),
    priority,
    confidence,
    ...(row['reason'] === undefined ? {} : { reason: string(row['reason']) }),
    groups: array(row['groups']).map((value) => {
      const group = object(value);
      const resolutionMode = group['resolution_mode'];
      if (resolutionMode !== 'changes' && resolutionMode !== 'conflict') {
        throw new Error('Invalid suggestion resolution mode');
      }
      return {
        id: string(group['id']),
        resolutionMode,
        targets: array(group['targets']).map((value) => {
          const target = object(value);
          const contentType = target['content_type'];
          if (contentType !== 'page' && contentType !== 'item')
            throw new Error('Invalid suggestion target');
          return {
            id: string(target['id']),
            contentId: string(target['content_id']),
            contentType,
            folder: string(target['folder']),
            title: string(target['title']),
            variants: array(target['variants']).map((value) => {
              const variant = object(value);
              return {
                locale: string(variant['locale']),
                current: string(variant['current']),
                ...(variant['suggested'] === undefined
                  ? {}
                  : { suggested: string(variant['suggested']) }),
              };
            }),
          };
        }),
      };
    }),
  };
}

export function normalizeSuggestionRun(value: unknown): SuggestionRun {
  const run = object(value);
  const status = run['status'];
  if (
    status !== 'queued' &&
    status !== 'snapshotting' &&
    status !== 'analysing' &&
    status !== 'validating' &&
    status !== 'succeeded' &&
    status !== 'failed'
  ) {
    throw new Error('Invalid analysis status');
  }
  const error = run['error'] == null ? undefined : object(run['error']);
  return {
    id: string(run['id']),
    status,
    ...(error
      ? {
          error: {
            code: string(error['code']),
            message: string(error['message']),
          },
        }
      : {}),
  };
}

class AnalysisConflict extends Error {
  constructor(readonly runId: string) {
    super('An analysis already exists');
  }
}

export function normalizeSuggestionSet(value: unknown): SuggestionSet {
  const set = object(value);
  const meta = object(set['meta']);
  const snapshotCapturedAt =
    meta['snapshot_captured_at'] === null
      ? null
      : string(meta['snapshot_captured_at']);
  if (
    snapshotCapturedAt !== null &&
    !Number.isFinite(Date.parse(snapshotCapturedAt))
  )
    throw new Error('Invalid capture time');
  return {
    latestRun:
      meta['latest_run'] == null
        ? null
        : normalizeSuggestionRun(meta['latest_run']),
    runId: meta['run_id'] === null ? null : string(meta['run_id']),
    snapshotCapturedAt,
    suggestions: array(set['content_suggestions'], true).flatMap(
      (row, index) => {
        try {
          return [normalizeSuggestion(row)];
        } catch (error) {
          console.warn('Skipping malformed content suggestion', {
            runId: meta['run_id'],
            index,
            error,
          });
          return [];
        }
      }
    ),
  };
}

export default class ContentSuggestionAdapter extends ApplicationAdapter {
  async list(hotelId: string) {
    return normalizeSuggestionSet(await this.ajax(this.url(hotelId), 'GET'));
  }

  async detail(hotelId: string, id: string) {
    const response = object(
      await this.ajax(`${this.url(hotelId)}/${encodeURIComponent(id)}`, 'GET')
    );
    return normalizeSuggestion(response['content_suggestion']);
  }

  async startAnalysis(hotelId: string, requestId: string) {
    try {
      const response = object(
        await this.ajax(this.runsUrl(hotelId), 'POST', {
          data: { request_id: requestId },
        })
      );
      return normalizeSuggestionRun(response['content_suggestion_run']);
    } catch (error) {
      if (error instanceof AnalysisConflict)
        return { id: error.runId, status: 'queued' as const };
      throw error;
    }
  }

  async analysis(hotelId: string, id: string) {
    const response = object(
      await this.ajax(
        `${this.runsUrl(hotelId)}/${encodeURIComponent(id)}`,
        'GET'
      )
    );
    const run = normalizeSuggestionRun(response['content_suggestion_run']);
    if (run.id !== id) throw new Error('Unexpected analysis ID');
    return run;
  }

  handleResponse(...args: Parameters<ApplicationAdapter['handleResponse']>) {
    if (args[0] === 409) {
      const payload = object(args[2]);
      throw new AnalysisConflict(string(payload['run_id']));
    }
    return super.handleResponse(...args);
  }

  private runsUrl(hotelId: string) {
    return `${this.buildURL('hotel', hotelId)}/content_suggestion_runs`;
  }

  private url(hotelId: string) {
    return `${this.buildURL('hotel', hotelId)}/content_suggestions`;
  }
}

declare module 'ember-data/types/registries/adapter' {
  export default interface AdapterRegistry {
    'content-suggestion': ContentSuggestionAdapter;
  }
}
